import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { PHOTOS } from '../mock';
import { adminApi } from '../api';
import { resizeImageFile, isImageFile } from '../lib/imageResize';
import { Image as ImageIcon, Plus, X, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE_KEYS = ['wedding', 'portrait', 'corporate', 'birthday'];

const AddPhotosDialog = ({ open, onOpenChange, subgalleryId, subgalleryTitle, onAdded }) => {
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [reading, setReading] = useState(false);

  const readFiles = async (files) => {
    const list = Array.from(files || []);
    const images = list.filter(isImageFile);
    const skipped = list.length - images.length;
    if (skipped > 0) toast.error(`${skipped} file ignorati (non sono immagini)`);
    if (images.length === 0) return;
    setReading(true);
    let ok = 0;
    for (const file of images) {
      try {
        const { dataUrl, filename } = await resizeImageFile(file);
        const uid = `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        setUploaded(prev => [...prev, { uid, data_url: dataUrl, filename }]);
        ok += 1;
      } catch (err) {
        toast.error(`Impossibile leggere ${file.name}`);
      }
    }
    setReading(false);
    if (ok > 0) toast.success(`${ok} foto pronte`);
  };

  const addSample = (key) => {
    const list = PHOTOS[key].map((u,i)=>({ uid: `s-${Date.now()}-${i}`, data_url: u, filename: `${key}_${Date.now()}_${i+1}.jpg` }));
    setUploaded(prev => [...prev, ...list]);
    toast.success(`${list.length} foto d'esempio aggiunte`);
  };

  const removePhoto = (i) => setUploaded(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (uploaded.length === 0) return toast.error('Aggiungi almeno una foto');
    setBusy(true); setProgress(0);
    try {
      let done = 0;
      for (const p of uploaded) {
        await adminApi.addPhoto(subgalleryId, { filename: p.filename, data_url: p.data_url });
        done += 1; setProgress(Math.round((done / uploaded.length) * 100));
      }
      toast.success(`${uploaded.length} foto aggiunte a "${subgalleryTitle}"`);
      onAdded && await onAdded();
      onOpenChange(false);
      setUploaded([]); setProgress(0);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore caricamento'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v && !busy) setUploaded([]); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Upload className="w-5 h-5 text-[#B8944B]" /> Aggiungi foto</DialogTitle>
          <DialogDescription>Carica altre foto in <b>{subgalleryTitle}</b>. Le foto verranno aggiunte in coda a quelle esistenti.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Nuove foto ({uploaded.length})</span>
              <div className="flex items-center gap-2">
                {SAMPLE_KEYS.map(k => (
                  <button key={k} type="button" onClick={()=>addSample(k)} className="text-[10px] uppercase tracking-widest text-[#B8944B] hover:underline">demo {k}</button>
                ))}
              </div>
            </div>
            <label
              onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
              onDragLeave={()=>setDragOver(false)}
              onDrop={(e)=>{ e.preventDefault(); setDragOver(false); readFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-8 cursor-pointer transition ${dragOver ? 'border-[#C9A961] bg-[#FAF5E6]/70' : 'border-slate-200 hover:border-[#C9A961] hover:bg-[#FAF5E6]/40'}`}>
              {reading ? <Loader2 className="w-8 h-8 text-[#C9A961] mb-2 animate-spin" /> : <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />}
              <span className="text-sm text-slate-600">{reading ? 'Preparo le foto...' : 'Clicca o trascina qui le foto'}</span>
              <span className="text-xs text-slate-400 mt-1">JPG, PNG, HEIC — foto grandi ridimensionate automaticamente in alta qualità</span>
              <input data-testid="add-photos-file-input" type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={e=>{ readFiles(e.target.files); e.target.value=''; }} />
            </label>
            {uploaded.length > 0 && (
              <div className="grid grid-cols-6 gap-2 mt-3">
                {uploaded.slice(0, 24).map((u, i) => (
                  <div key={u.uid || `${u.filename}-${i}`} className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative group">
                    <img src={u.data_url} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={()=>removePhoto(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {uploaded.length > 24 && <div className="aspect-square rounded-lg bg-slate-100 grid place-items-center text-xs text-slate-500">+{uploaded.length - 24}</div>}
              </div>
            )}
          </div>

          {busy && (
            <div className="bg-[#F7F1E1] border border-[#C9A961]/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm text-[#8A7434] mb-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento foto... {progress}%</div>
              <div className="h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-[#C9A961] transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" disabled={busy} onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50">Annulla</button>
            <button disabled={busy || uploaded.length === 0} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-50">
              <Plus className="w-4 h-4" /> {busy ? 'Caricamento...' : `Aggiungi ${uploaded.length > 0 ? `(${uploaded.length})` : ''}`}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPhotosDialog;
